import React, { useState, useEffect } from 'react';
import { Settings, Save, RefreshCw, Brain, MessageCircle, Phone, Globe, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Navbar from '../components/Navbar';
import api from '../services/api';
import { toast } from 'sonner';

export default function AIConfigPage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    loadConfig();
    loadAnalytics();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await api.get('/ai/config');
      setConfig(response.data);
    } catch (error) {
      console.error('Error loading AI config:', error);
      toast.error('Failed to load AI configuration');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await api.get('/ai/analytics');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/ai/config', config);
      toast.success('AI configuration saved successfully!');
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Navbar />
        <div className="max-w-6xl mx-auto px-6 py-12 text-center">
          <div className="animate-spin h-8 w-8 border-4 border-[#2DD4BF] border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-[#4B5563]">Loading AI configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar />

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#0F2A44] rounded-xl flex items-center justify-center">
              <Brain className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="font-manrope font-bold text-2xl text-[#0F2A44]">
                AI Configuration
              </h1>
              <p className="font-public text-[#4B5563]">
                Manage AI-powered features and settings
              </p>
            </div>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#2DD4BF] hover:bg-[#2DD4BF]/90 text-[#0F2A44] font-semibold"
          >
            {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Changes
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Settings */}
          <div className="lg:col-span-2 space-y-6">
            {/* Feature Toggles */}
            <div className="bg-white rounded-xl border border-slate-100 p-6">
              <h2 className="font-manrope font-semibold text-lg text-[#0F2A44] mb-4 flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Feature Toggles
              </h2>
              <div className="space-y-4">
                {[
                  { key: 'symptom_checker_enabled', label: 'AI Symptom Checker', icon: MessageCircle, description: 'Conversational symptom analysis' },
                  { key: 'booking_agent_enabled', label: 'AI Booking Agent', icon: Phone, description: 'Automated appointment booking' },
                  { key: 'voice_enabled', label: 'Voice Interaction', icon: Phone, description: 'Speech-to-text and text-to-speech (Phase 2)' },
                  { key: 'ussd_enabled', label: 'USSD Interface', icon: Phone, description: 'Non-smartphone access (Phase 2)' },
                  { key: 'whatsapp_enabled', label: 'WhatsApp Integration', icon: MessageCircle, description: 'WhatsApp chatbot (Phase 2)' },
                  { key: 'red_flag_escalation', label: 'Red Flag Detection', icon: AlertTriangle, description: 'Emergency symptom detection' },
                  { key: 'auto_recommend_specialty', label: 'Auto Specialty Recommendation', icon: Brain, description: 'Automatic specialty matching' },
                ].map(({ key, label, icon: Icon, description }) => (
                  <div key={key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Icon className="h-5 w-5 text-[#4A90E2]" />
                      <div>
                        <p className="font-medium text-[#0F2A44]">{label}</p>
                        <p className="text-xs text-[#4B5563]">{description}</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={config?.[key] || false}
                        onChange={(e) => updateConfig(key, e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#2DD4BF]"></div>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Model Settings */}
            <div className="bg-white rounded-xl border border-slate-100 p-6">
              <h2 className="font-manrope font-semibold text-lg text-[#0F2A44] mb-4 flex items-center gap-2">
                <Brain className="h-5 w-5" />
                AI Model Settings
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#262626] mb-2">Model</label>
                  <select
                    value={config?.model || 'gpt-4o-mini'}
                    onChange={(e) => updateConfig('model', e.target.value)}
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:border-[#2DD4BF] focus:ring-1 focus:ring-[#2DD4BF]"
                  >
                    <option value="gpt-4o-mini">GPT-4o Mini (Recommended)</option>
                    <option value="gpt-4o">GPT-4o (More capable, higher cost)</option>
                    <option value="gpt-4">GPT-4 (Legacy)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#262626] mb-2">Temperature (0-1)</label>
                  <Input
                    type="number"
                    min="0"
                    max="1"
                    step="0.1"
                    value={config?.temperature || 0.7}
                    onChange={(e) => updateConfig('temperature', parseFloat(e.target.value))}
                  />
                  <p className="text-xs text-[#4B5563] mt-1">Higher = more creative, Lower = more focused</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#262626] mb-2">Max Tokens</label>
                  <Input
                    type="number"
                    min="100"
                    max="2000"
                    value={config?.max_tokens || 500}
                    onChange={(e) => updateConfig('max_tokens', parseInt(e.target.value))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#262626] mb-2">Default Language</label>
                  <select
                    value={config?.default_language || 'en'}
                    onChange={(e) => updateConfig('default_language', e.target.value)}
                    className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:border-[#2DD4BF] focus:ring-1 focus:ring-[#2DD4BF]"
                  >
                    <option value="en">English</option>
                    <option value="sw">Kiswahili</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Custom Prompt */}
            <div className="bg-white rounded-xl border border-slate-100 p-6">
              <h2 className="font-manrope font-semibold text-lg text-[#0F2A44] mb-4">
                Custom System Prompt Additions
              </h2>
              <Textarea
                value={config?.system_prompt_additions || ''}
                onChange={(e) => updateConfig('system_prompt_additions', e.target.value)}
                placeholder="Add custom instructions for the AI (e.g., specific guidelines, tone adjustments, additional context)..."
                className="min-h-[120px]"
              />
              <p className="text-xs text-[#4B5563] mt-2">
                These instructions will be appended to the base system prompt for all AI interactions.
              </p>
            </div>

            {/* Disclaimer Text */}
            <div className="bg-white rounded-xl border border-slate-100 p-6">
              <h2 className="font-manrope font-semibold text-lg text-[#0F2A44] mb-4">
                Disclaimer Text
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#262626] mb-2">English Disclaimer</label>
                  <Textarea
                    value={config?.disclaimer_text?.en || ''}
                    onChange={(e) => updateConfig('disclaimer_text', { ...config?.disclaimer_text, en: e.target.value })}
                    placeholder="Medical disclaimer in English..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#262626] mb-2">Swahili Disclaimer</label>
                  <Textarea
                    value={config?.disclaimer_text?.sw || ''}
                    onChange={(e) => updateConfig('disclaimer_text', { ...config?.disclaimer_text, sw: e.target.value })}
                    placeholder="Medical disclaimer in Swahili..."
                  />
                </div>
              </div>
            </div>

            {/* Emergency Numbers */}
            <div className="bg-white rounded-xl border border-slate-100 p-6">
              <h2 className="font-manrope font-semibold text-lg text-[#0F2A44] mb-4 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                Emergency Contact Numbers
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#262626] mb-2">Ambulance</label>
                  <Input
                    value={config?.emergency_numbers?.ambulance || '999'}
                    onChange={(e) => updateConfig('emergency_numbers', { ...config?.emergency_numbers, ambulance: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#262626] mb-2">Police</label>
                  <Input
                    value={config?.emergency_numbers?.police || '999'}
                    onChange={(e) => updateConfig('emergency_numbers', { ...config?.emergency_numbers, police: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#262626] mb-2">Fire</label>
                  <Input
                    value={config?.emergency_numbers?.fire || '999'}
                    onChange={(e) => updateConfig('emergency_numbers', { ...config?.emergency_numbers, fire: e.target.value })}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Analytics Sidebar */}
          <div className="space-y-6">
            {/* Analytics Overview */}
            <div className="bg-white rounded-xl border border-slate-100 p-6">
              <h2 className="font-manrope font-semibold text-lg text-[#0F2A44] mb-4">
                AI Analytics
              </h2>
              {analytics ? (
                <div className="space-y-4">
                  <div className="p-4 bg-[#2DD4BF]/10 rounded-lg text-center">
                    <p className="text-3xl font-bold text-[#0F2A44]">{analytics.total_conversations}</p>
                    <p className="text-sm text-[#4B5563]">Total Conversations</p>
                  </div>
                  
                  <div className="p-4 bg-red-50 rounded-lg">
                    <p className="text-xl font-bold text-red-600">{analytics.red_flag_conversations}</p>
                    <p className="text-sm text-red-700">Red Flag Alerts</p>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-[#262626] mb-2">Urgency Breakdown</h3>
                    <div className="space-y-2">
                      {Object.entries(analytics.urgency_breakdown || {}).map(([level, count]) => (
                        <div key={level} className="flex justify-between items-center text-sm">
                          <span className="capitalize text-[#4B5563]">{level}</span>
                          <span className="font-medium">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-[#262626] mb-2">Top Specialties</h3>
                    <div className="space-y-2">
                      {Object.entries(analytics.top_specialties || {}).slice(0, 5).map(([specialty, count]) => (
                        <div key={specialty} className="flex justify-between items-center text-sm">
                          <span className="text-[#4B5563] truncate">{specialty}</span>
                          <span className="font-medium">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-[#262626] mb-2">Language Usage</h3>
                    <div className="space-y-2">
                      {Object.entries(analytics.language_usage || {}).map(([lang, count]) => (
                        <div key={lang} className="flex justify-between items-center text-sm">
                          <span className="text-[#4B5563]">{lang === 'en' ? 'English' : 'Kiswahili'}</span>
                          <span className="font-medium">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-center text-[#4B5563] py-4">No analytics data yet</p>
              )}
              <Button
                onClick={loadAnalytics}
                variant="outline"
                className="w-full mt-4"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Analytics
              </Button>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl border border-slate-100 p-6">
              <h2 className="font-manrope font-semibold text-lg text-[#0F2A44] mb-4">
                Quick Actions
              </h2>
              <div className="space-y-3">
                <Button
                  onClick={() => window.open('/terms', '_blank')}
                  variant="outline"
                  className="w-full justify-start"
                >
                  View Terms & Conditions
                </Button>
                <Button
                  onClick={loadConfig}
                  variant="outline"
                  className="w-full justify-start"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reload Configuration
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
